import MetalClient, { envTypes } from '@atlassiansox/metal-client';
import getMeta from '@atlassian/jira-get-meta';

// Provided by Observability
const JIRA_FRONTEND_METAL_ID = '74e9584c-1c80-4770-868c-41ff535cc118';

// id of element where application is mounted
const JIRA_FRONTEND_ROOT_ID = 'jira-frontend';

// Singleton instance of the METAL client
let metalInstance: MetalClient | undefined;

// This allows consumers to get a promise that will resolve to the METAL client once it is
// initialised.
let metalPromiseResolve: (v: MetalClient) => void;
const metalPromise: Promise<MetalClient> = new Promise((resolve) => {
	metalPromiseResolve = resolve;
});
export const getMetalAsync = () => metalPromise;
export const DEV = 'dev' as const;
type Context = {
	environment: string;
};
/**
 * Sets up the MetalClient with the correct configuration based on the current environment.
 * It ensures that only one instance of MetalClient is active, supporting consistent interaction
 * across the application. This setup includes selecting the appropriate environment and
 * configuring settings related to synthetic events.
 */
export const initMetal = ({ environment }: Context): MetalClient => {
	if (metalInstance) {
		// If we've already initialised METAL, then this is a no-op. Why isn't it an error? Because
		// we initialise from AppBase and there can currently be multiple AppBase instances on the page
		return metalInstance;
	}
	let metalEnvironment;
	switch (environment) {
		case 'staging':
			metalEnvironment = envTypes.STAGING;
			break;
		case 'prod':
			metalEnvironment = envTypes.PROD;
			break;
		case 'dev':
		default:
			metalEnvironment = envTypes.DEV;
	}
	const config = {
		productInfo: {
			metalId: JIRA_FRONTEND_METAL_ID,
			rootId: JIRA_FRONTEND_ROOT_ID,
			env: metalEnvironment,
			version: window.BUILD_KEY,
		},
		settings: {
			synthetic: getMeta('ajs-is-synthetic') === 'true',
		},
	};
	metalInstance = new MetalClient(config);
	metalPromiseResolve(metalInstance);
	return metalInstance;
};
export const getMetal = (): MetalClient => {
	if (!metalInstance) {
		throw new Error(
			'getMetal called before client was initialised. You may want to consider using `getMetalAsync`',
		);
	}
	return metalInstance;
};

// This is mostly used for testing, it is not expected that under regular use you'd want to destroy the client
export const destroyMetal = () => {
	metalInstance = undefined;
};
